import { useEffect, useRef, useState } from 'react'

import { useCreateIncidentRules } from '../hooks/useCreateIncidentRules'
import { useMenuPlacement } from '../hooks/useMenuPlacement'
import { SubmitHandler, useForm } from 'react-hook-form'
import { HiOutlineCheckCircle } from 'react-icons/hi'

import { Box, Flex, ScaleFade } from '@chakra-ui/react'

import {
  Button,
  FormDatePickerControl,
  FormInputSelectControl,
  FormRadioInputControl,
  FormTextareaControl,
} from '@/components/ui'
import { Action, RepeatInterval } from '@/graphql/generated/schemas'

import { AiOperatorCriteria } from '../ai-operator-criteria/components/AiOperatorCriteria'
import { Criterion } from '../ai-operator-criteria/types/types'
import { FormInputsT } from '../types/types'
import {
  MAX_RULENAME_LENGTH,
  actionOptions,
  isEmailActionValid,
} from '../utils/utils'
import { CrossDayAlert } from './CrossDayAlert'
import { EmailInputSelectControl } from './EmailInputSelectControl'
import { LabeledSwitch } from './LabeledSwitch'
import { RecurrenceToggle } from './RecurrenceToggle'
import { RuleScheduleAlert } from './RuleScheduleAlert'
import { RuleSection } from './RuleSection'

export const CreateIncidentRules = () => {
  const scrollViewRef = useRef<HTMLDivElement>(null)
  const deviceRef = useRef<HTMLDivElement>(null)
  const deviceMenuPlacement = useMenuPlacement(scrollViewRef, deviceRef)
  const [aiOperatorCriteria, setAiOperatorCriteria] = useState<Criterion[]>([])

  const form = useForm<FormInputsT>({
    mode: 'onChange',
  })
  const {
    register,
    handleSubmit,
    control,
    formState,
    watch,
    trigger,
    getValues,
    reset,
  } = form
  const { isValid } = formState
  const watchFacility = watch('facility')
  const watchFloor = watch('floor')
  const watchRepeatDevice = watch('device')
  const watchRepeatEvents = watch('event')
  const watchRepeatType = watch('repeatType')
  const watchAction = watch('action')
  const watchEmails = watch('emails')

  const isAiOperatorActionSelected =
    watchAction?.value === Action.EnableAiOperator

  const {
    eventOptions,
    shouldIncludeAllFloors,
    shouldIncludeAllDevices,
    shouldIncludeAllEvents,
    selectedStartDate,
    selectedEndDate,
    selectedEndTime,
    shouldShowDays,
    shouldRepeat,
    shouldEnd,
    days,
    timeZone,
    setShouldIncludeAllDevices,
    setSelectedStartDate,
    setSelectedEndDate,
    updateEndTime,
    updateDate,
    setShouldIncludeAllEvents,
    setShouldRepeat,
    setShouldShowDays,
    setShouldEnd,
    facilityOptions,
    floorOptions,
    deviceOptions,
    emailOptions,
    isLoading,
    isFloorsLoading,
    isDevicesLoading,
    updateSelectedFacilityId,
    updateToggleAllFloors,
    updateSelectedFloorIds,
    updateNumberOfRepeats,
    updateSelectedDays,
    createRule,
    isActionOptionDisabled,
    isAllDay,
    toggleAllDaySwitch,
  } = useCreateIncidentRules({
    form,
    criteria: aiOperatorCriteria,
    isActionAiOperator: isAiOperatorActionSelected,
  })

  const onSubmit: SubmitHandler<FormInputsT> = async (values) => {
    createRule(values)
  }

  const areDevicesSelected =
    (shouldIncludeAllDevices && deviceOptions.length > 0) ||
    watchRepeatDevice?.length > 0
  const areEventsSelected =
    shouldIncludeAllEvents || watchRepeatEvents?.length > 0
  const isEndDateEnabledAndSelected = shouldEnd && !selectedEndDate

  const isAiOperatorCriteriaSelected =
    isAiOperatorActionSelected && aiOperatorCriteria.length === 0

  const isCreateDisabled =
    !isValid ||
    !areDevicesSelected ||
    !areEventsSelected ||
    !isEmailActionValid(watchAction?.value, watchEmails) ||
    (!isAllDay && !selectedEndTime) ||
    isEndDateEnabledAndSelected ||
    isAiOperatorCriteriaSelected

  const shouldShowCrossDayAlert =
    !isAllDay && selectedStartDate && selectedEndTime

  useEffect(() => {
    updateSelectedFacilityId(watchFacility)
  }, [watchFacility])

  useEffect(() => {
    updateSelectedFloorIds(watchFloor)
  }, [watchFloor])

  useEffect(() => {
    if (watchRepeatType) {
      const shouldShowDays = watchRepeatType.value === RepeatInterval.Weekly
      setShouldShowDays(shouldShowDays)
    }
  }, [watchRepeatType])

  useEffect(() => {
    // Clear values if AiOperator or Mute action is selected
    const watchActionValue = watchAction?.value
    if (
      watchActionValue === Action.EnableAiOperator ||
      watchActionValue === Action.Mute
    ) {
      reset({
        ...getValues(),
        event: null,
      })
    }
    trigger('event')
  }, [watchAction])

  return (
    <Flex direction='column' h='calc(100vh - 85px)'>
      <Flex
        direction='row'
        gap={{ base: '2px', md: '2px', lg: '6px' }}
        h='100%'
        overflowY='scroll'
        py='20px'
        ref={scrollViewRef}
        w='full'
      >
        {/* Column 1: Name, Facilities, Floors, Devices */}
        <Flex
          direction='column'
          flex={{ base: '1', lg: '0.9' }}
          px={{ base: '16px', md: '24px' }}
        >
          <RuleSection mb={6}>
            <FormTextareaControl
              id='name'
              label='Name of rule'
              labelColour='#1A202C'
              labelFontWeight='600'
              labelOpacity={1}
              maxCharCount={MAX_RULENAME_LENGTH}
              placeholder='Enter a name for this rule'
              textareaRef={register({
                maxLength: {
                  value: MAX_RULENAME_LENGTH,
                  message: `Rule name should have less than ${MAX_RULENAME_LENGTH} characters`,
                },
                required: 'Rule name is required',
              })}
            />
          </RuleSection>

          <RuleSection label='Select the devices associated with the rule'>
            <Box mb={6}>
              <FormInputSelectControl
                control={control}
                defaultValue={null}
                id='facility'
                isClearable
                isDisabled={false}
                isLoading={isLoading}
                isSearchable
                label='Facility'
                labelColour='#1A202C'
                labelFontWeight='600'
                labelOpacity={1}
                options={facilityOptions}
                rules={{ required: true }}
              />
            </Box>
            <Box mb={6}>
              <FormRadioInputControl
                control={control}
                dropdownContainerHeight='200px'
                id='floor'
                isLoading={isFloorsLoading}
                label='Floor'
                leftRadioButtonLabel='All floors'
                options={floorOptions}
                rightRadioButtonLabel='Select one or more floors'
                setToggleAll={(isToggled) =>
                  updateToggleAllFloors(isToggled, watchFloor)
                }
                toggleAll={shouldIncludeAllFloors}
              />
            </Box>
            <Box ref={deviceRef}>
              <FormRadioInputControl
                control={control}
                dropdownContainerHeight='200px'
                id='device'
                isLoading={isDevicesLoading}
                label='Door Device'
                leftRadioButtonLabel='All devices'
                menuPlacement={deviceMenuPlacement}
                options={deviceOptions}
                rightRadioButtonLabel='Select one or more devices'
                setToggleAll={setShouldIncludeAllDevices}
                toggleAll={shouldIncludeAllDevices}
              />
            </Box>
          </RuleSection>
        </Flex>

        {/* Column 2: Event, Actions Schedule */}
        <Flex
          direction='column'
          flex={{ base: '1', lg: '1.1' }}
          px={{ base: '16px', md: '24px' }}
        >
          <RuleSection label='Rule Actions' mb={6}>
            <Box mb={6}>
              <FormInputSelectControl
                control={control}
                defaultValue={null}
                id='action'
                isClearable
                isDisabled={false}
                isLoading={isLoading}
                isOptionDisabled={isActionOptionDisabled}
                isSearchable
                label='Choose Action'
                labelColour='#1A202C'
                labelFontWeight='600'
                labelOpacity={1}
                options={actionOptions}
                rules={{ required: true }}
              />
            </Box>

            <EmailInputSelectControl
              control={control}
              id='emails'
              isLoading={isLoading}
              isOpen={watchAction?.value == Action.SendEmail}
              label='Recipients'
              options={emailOptions}
              placeholder='Add Recipients'
            />
            {isAiOperatorActionSelected && (
              <ScaleFade
                in={true}
                initialScale={0.95}
                transition={{ enter: { duration: 0.3 } }}
              >
                <AiOperatorCriteria onChange={setAiOperatorCriteria} />
              </ScaleFade>
            )}

            <FormRadioInputControl
              control={control}
              id='event'
              label='Event'
              leftRadioButtonLabel='All events'
              options={eventOptions}
              rightRadioButtonLabel='Select one or more events'
              setToggleAll={setShouldIncludeAllEvents}
              toggleAll={shouldIncludeAllEvents}
            />
          </RuleSection>

          <RuleSection label='Select time frame for the action to be applied'>
            <RuleScheduleAlert
              facilityName={watchFacility?.label}
              timeZone={timeZone}
            />

            {shouldShowCrossDayAlert && (
              <CrossDayAlert
                endTime={selectedEndTime}
                startTime={selectedStartDate}
              />
            )}

            <Box mb={6}>
              <LabeledSwitch
                id='all-day-switch'
                inputRef={null}
                isChecked={isAllDay}
                label='All Day'
                onSwitchChange={toggleAllDaySwitch}
              />
              <FormDatePickerControl
                control={control}
                id='date'
                isAllDay={isAllDay}
                onChangeDate={updateDate}
                onChangeEndTime={updateEndTime}
                onChangeStartTime={setSelectedStartDate}
                selectedDate={selectedStartDate}
                selectedEndTime={selectedEndTime}
                showEndTimePicker
                showStartTimePicker
              />
            </Box>

            <RecurrenceToggle
              control={control}
              days={days}
              endDate={selectedEndDate}
              onEndDateChange={setSelectedEndDate}
              onRepeatValueChange={updateNumberOfRepeats}
              selectedStartDate={selectedStartDate}
              setDays={updateSelectedDays}
              setShouldEnd={setShouldEnd}
              setShouldRepeat={setShouldRepeat}
              shouldEnd={shouldEnd}
              shouldRepeat={shouldRepeat}
              shouldShowDays={shouldShowDays}
            />
          </RuleSection>
        </Flex>
      </Flex>

      <Flex justifyContent='flex-end' mb='40px' px='40px'>
        <Button
          isDisabled={isCreateDisabled}
          isLoading={isLoading}
          leftIcon={<HiOutlineCheckCircle size='24px' />}
          loadingText='Creating Rule'
          onClick={handleSubmit(onSubmit)}
          type='submit'
        >
          Create Rule
        </Button>
      </Flex>
    </Flex>
  )
}
